import { useWorkflow } from 'features/libra/WorkflowContext'
import { useCallback, useMemo } from 'react'

import { OntologyField } from './ontologyFieldTypes'

interface OntologyFieldsResult {
  ontologyFields: Record<string, OntologyField>
  getField: (fieldId: string) => OntologyField | undefined
}

/**
 * Hook to extract and work with ontology fields from the workflow's globalContext.
 * Provides the ontology fields and a helper function to retrieve individual fields.
 */
export const useOntologyFields = (): OntologyFieldsResult => {
  const workflow = useWorkflow()

  const ontologyFields = useMemo(() => {
    const fields = workflow.globalContext.ontologyFields
    if (!fields || typeof fields !== 'object') {
      return {}
    }
    return fields as Record<string, OntologyField>
  }, [workflow.globalContext.ontologyFields])

  const getField = useCallback(
    (fieldId: string): OntologyField | undefined => {
      return ontologyFields[fieldId]
    },
    [ontologyFields]
  )

  return useMemo(
    () => ({
      ontologyFields,
      getField,
    }),
    [ontologyFields, getField]
  )
}
